import { Component, EventEmitter, Output, AfterViewInit, NgZone } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { Preventivo, DatiAzienda, DatiLegale } from '../shared/preventivo';
import { Googleapi } from '../shared/googleapi';
import { CompanyType } from '../shared/company-type';

import { PrefissiInternazionali } from '../shared/prefissi-internazionali';


declare const google: any;

@Component({
  selector: 'app-step4',
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './step4.html',
  styleUrl: './step4.css'
})
export class Step4 implements AfterViewInit {
  @Output() avanti = new EventEmitter<void>();

  // Dati aziendali
  ragioneSociale: string = '';
  tipoAzienda: string = '';
  partitaIva: string = '';
  indirizzoCompletoAzienda: string = '';
  indirizzoAzienda: string = '';
  comuneAzienda: string = '';
  capAzienda: string = '';
  provinciaAzienda: string = '';
  civicoAzienda: string = '';

  // Rappresentante legale
  codiceFiscale: string = '';
  nome: string = '';
  cognome: string = '';
  dataNascita: string = '';
  comuneNascita: string = '';
  genere: string = '';
  email: string = '';
  prefisso: string = '+39';
  telefono: string = '';
  indirizzoCompletoResidenza: string = '';
  indirizzoResidenza: string = '';
  comuneResidenza: string = '';
  capResidenza: string = '';
  provinciaResidenza: string = '';
  civicoResidenza: string = '';

  tipiAzienda: string[] = [];

  prefissiInternazionali: { flag: string, value: string }[] = [];

  privacyConsenso: boolean = false; 
  marketingConsenso: boolean = false;

  constructor(
    private preventivoService: Preventivo, 
    private ngZone: NgZone, 
    private googleapiService: Googleapi,
    private companyTypeService: CompanyType,
    private prefissiService: PrefissiInternazionali
  ) {
    const anagrafica = preventivoService.getDatiAnagrafici();
    if (anagrafica) {
      this.ragioneSociale = anagrafica.ragioneSociale;
      this.nome = anagrafica.nome;
      this.cognome = anagrafica.cognome;
      this.email = anagrafica.email;
      this.prefisso = anagrafica.prefisso ?? '+39';
      this.telefono = anagrafica.telefono ?? '';
    }

    this.prefissiInternazionali = this.prefissiService.getPrefissi();
    this.privacyConsenso = preventivoService.getPrivacyConsenso();
    this.marketingConsenso = preventivoService.getMarketingConsenso();
    this.tipiAzienda = this.companyTypeService.getCompanyType();
  }

  async ngAfterViewInit() {
    await this.googleapiService.load();

    const input = document.getElementById('autocompleteAzienda') as HTMLInputElement;
    const inputResidenza = document.getElementById('autocompleteResidenza') as HTMLInputElement;

    if (input && google) {
      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'it' }
      });

      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place = autocomplete.getPlace();

          // Debug
          //console.log('▶️ Google Place object:', place);

          if (!place.address_components) return;

          const get = (type: string, short: boolean = false) =>
            place.address_components.find((c: any) => c.types.includes(type))?.[short ? 'short_name' : 'long_name'] || '';

          const route = get('route');
          const streetNumber = get('street_number');
          const cap = get('postal_code');
          const comune = get('locality');
          const provincia = get('administrative_area_level_2', true);

          this.indirizzoAzienda = route;
          this.civicoAzienda = streetNumber;
          this.capAzienda = cap;
          this.comuneAzienda = comune;
          this.provinciaAzienda = provincia;

          this.indirizzoCompletoAzienda = `${route} ${streetNumber}, ${cap} ${comune} (${provincia})`;
        });
      });
    }
    if (inputResidenza && google) {
      const autocompleteResidenza = new google.maps.places.Autocomplete(inputResidenza, {
        types: ['address'],
        componentRestrictions: { country: 'it' }
      });

      autocompleteResidenza.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place = autocompleteResidenza.getPlace();

          const get = (type: string, short = false) =>
            place.address_components.find((c: any) => c.types.includes(type))?.[short ? 'short_name' : 'long_name'] || '';

          const route = get('route');
          const streetNumber = get('street_number');
          const cap = get('postal_code');
          const comune = get('locality');
          const provinciaSigla = get('administrative_area_level_2', true);

          this.indirizzoResidenza = route;
          this.civicoResidenza = streetNumber;
          this.capResidenza = cap;
          this.comuneResidenza = comune;
          this.provinciaResidenza = provinciaSigla;

          this.indirizzoCompletoResidenza = `${route} ${streetNumber}, ${cap} ${comune} (${provinciaSigla})`;
        });
      });
    }
  }

  isValidIndirizzoAzienda(): boolean {
    return !!this.indirizzoCompletoAzienda &&
      !!this.indirizzoAzienda &&
      !!this.civicoAzienda &&
      !!this.comuneAzienda &&
      !!this.capAzienda &&
      !!this.provinciaAzienda;
  }

  isValidIndirizzoResidenza(): boolean {
    return !!this.indirizzoCompletoResidenza &&
      !!this.indirizzoResidenza &&
      !!this.civicoResidenza &&
      !!this.comuneResidenza &&
      !!this.capResidenza &&
      !!this.provinciaResidenza;
  }

  isValid(): boolean {
    const telefonoValido = !!this.telefono && /^[0-9]{6,10}$/.test(this.telefono);
    const emailValida = !!this.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);

    const aziendaValida =
      !!this.ragioneSociale &&
      !!this.tipoAzienda &&
      !!this.partitaIva && this.partitaIva.length >= 11 &&
      this.isValidIndirizzoAzienda();

    const legaleValido =
      !!this.codiceFiscale &&
      !!this.nome &&
      !!this.cognome &&
      !!this.dataNascita &&
      !!this.comuneNascita &&
      !!this.genere &&
      !!this.email &&
      emailValida &&
      !!this.prefisso &&
      !!this.telefono &&
      telefonoValido &&
      this.isValidIndirizzoResidenza();

    return aziendaValida && legaleValido && this.privacyConsenso === true;
  }

  avantiStep() {
    if (!this.isValid()) {
      alert('Compila tutti i campi obbligatori.');
      return;
    }

    const datiAzienda = {
      ragioneSociale: this.ragioneSociale,
      tipoAzienda: this.tipoAzienda,
      partitaIva: this.partitaIva,
      indirizzoCompleto: this.indirizzoCompletoAzienda,
      indirizzo: this.indirizzoAzienda,
      comune: this.comuneAzienda,
      cap: this.capAzienda,
      provincia: this.provinciaAzienda,
      civico: this.civicoAzienda
    };

    const datiLegale = {
      codiceFiscale: this.codiceFiscale,
      cognome: this.cognome,
      nome: this.nome,
      dataNascita: this.dataNascita,
      comuneNascita: this.comuneNascita,
      genere: this.genere,
      email: this.email,
      prefisso: this.prefisso,
      telefono: this.telefono,
      indirizzoCompletoResidenza: this.indirizzoCompletoResidenza,
      indirizzoResidenza: this.indirizzoResidenza,
      comuneResidenza: this.comuneResidenza,
      capResidenza: this.capResidenza,
      provinciaResidenza: this.provinciaResidenza,
      civicoResidenza: this.civicoResidenza
    };

    this.preventivoService.setDatiAzienda(datiAzienda);
    this.preventivoService.setDatiLegale(datiLegale);
    this.preventivoService.setPrivacyConsenso(this.privacyConsenso);
    this.preventivoService.setMarketingConsenso(this.marketingConsenso);

    this.avanti.emit();
  }
}
